import { ThemeCss } from "gatsby-interface"
import { ColorSchemeCss } from "./color-schemes"
import { Alignment } from "./types"

// layout constants
// naming sucks, but it's a start
// sizes
export const maxSectionWidth = {
  small: "30rem", // 480px
  default: "40rem", // 640px
  sectionTitle: "54rem", // 864px
  tablet: "65rem", // 1040px
}
// maxWidths
// rem 52, 28, 8
// ch 48, 24

export const gap = {
  default: 7, // 24px
  desktop: 10, // 48px
  super: 12, // 64px
}

export const textLinearGradient = ({
  direction = 90,
  startColor,
  endColor,
}: {
  direction?: number
  startColor: string
  endColor: string
}) => ({
  background: `linear-gradient(${direction}deg, ${startColor}, ${endColor})`,
  WebkitBackgroundClip: `text`,
  WebkitTextFillColor: `transparent`,
  WebkitBoxDecorationBreak: `clone`, // fixes gradient bug over line break in Safari, see https://discourse.webflow.com/t/multi-line-gradient-text-safari-ios-fix-i-e-prevent-disappearing-text/135029
  MozBackgroundClip: "text",
  MozTextFillColor: "transparent",
  paddingBottom: `0.1em`, // with condensed line-height like 1.1 text clip cuts the bottom edges
  marginBottom: `-0.1em`, // of letters like "p" or "g", this temporary hack prevents that , but that needs more investigation
})

const emphasisForHeadline: ColorSchemeCss = theme => ({
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore: WebkitBoxDecorationBreak is not recognized as a CSS property for the Interpolation, but is necessary for the text gradient
  "b, strong": {
    color: theme.colorScheme.primary,
    fontWeight: `inherit`,
    fontStyle: `normal`,

    ...textLinearGradient({
      direction: theme.colorScheme.gradient.direction || 90,
      startColor: theme.colorScheme.gradient.start,
      endColor: theme.colorScheme.gradient.stop,
    }),
  },
})

// styles for the outer container of each SSLP component
// these are shared with the sub navigation and (most) section components.
// search "wrapperStyles" within this directory for the ones that do not use section
// (and more), and facilitate alignment with those elements
export const sectionCss: ColorSchemeCss = theme => ({
  background: theme.colorScheme.background,
  paddingTop: theme.space[8],
  paddingBottom: theme.space[8],

  [theme.mediaQueries.phablet]: {
    paddingTop: theme.space[10],
    paddingBottom: theme.space[10],
  },
})

export const eyebrowHeadingCss: ColorSchemeCss = theme => ({
  color: theme.colorScheme.accent,
  fontFamily: theme.fonts.body,
  fontSize: theme.fontSizes[1],
  fontWeight: theme.fontWeights.bold,
  letterSpacing: theme.letterSpacings.tracked,
  textTransform: "uppercase",
  // in case we're rendering as e.g. <p>
  margin: 0,
})

// hero title — slightly bigger than section
export const heroTitleCss: ColorSchemeCss = theme => [
  {
    color: theme.colorScheme.heading,
    fontFamily: theme.fonts.heading,
    fontSize: theme.fontSizes[7],
    fontWeight: theme.fontWeights.bold,
    lineHeight: 1.1, // to-do add token

    [theme.mediaQueries.phablet]: {
      fontSize: theme.fontSizes[9],
    },

    [theme.mediaQueries.desktop]: {
      fontSize: theme.fontSizes[10],
    },

    // TODO find out if this is in use … in Contentful :/
    span: {
      color: theme.colorScheme.heading,
    },
  },
  emphasisForHeadline(theme),
]

export const heroLedeCss: ColorSchemeCss = theme => ({
  fontSize: theme.fontSizes[2],
  lineHeight: theme.lineHeights.body,
  color: theme.colorScheme.text,
  [theme.mediaQueries.phablet]: {
    fontSize: theme.fontSizes[3],
  },
  "& *": {
    // for dangerouslySetInnerHTML
    // allows GATSBY_INVERTED theme to have white text otherwise follows default colors for RichTextBlock
    color: theme.colorScheme.text,
  },
  "& a": {
    color: theme.colorScheme.primary,
  },
})

// section title — slightly smaller than hero font size
export const sectionTitleCss: ColorSchemeCss = theme => [
  {
    color: theme.colorScheme.heading,
    fontFamily: theme.fonts.heading,
    fontSize: theme.fontSizes[6],
    fontWeight: theme.fontWeights.bold,
    lineHeight: 1.1, // to-do add token

    [theme.mediaQueries.phablet]: {
      fontSize: theme.fontSizes[8],
    },

    [theme.mediaQueries.desktop]: {
      fontSize: theme.fontSizes[9],
    },
  },
  emphasisForHeadline(theme),
]

// titles for section items
// currently that's `FeatureGrid` and `FeatureItems`
// suited for 2-3 items/row for phablet and up
export const itemTitleCss: ColorSchemeCss = theme => [
  {
    color: theme.colorScheme.heading,
    fontFamily: theme.fonts.heading,
    fontSize: theme.fontSizes[5],
    fontWeight: theme.fontWeights.bold,
    lineHeight: 1.1, // to-do add token

    [theme.mediaQueries.tablet]: {
      fontSize: theme.fontSizes[4],
    },

    [theme.mediaQueries.desktop]: {
      fontSize: theme.fontSizes[5],
    },
  },
  emphasisForHeadline(theme),
]

export const itemCss: ColorSchemeCss = theme => ({
  color: theme.colorScheme.text,
  fontSize: theme.fontSizes[2],

  a: {
    color: theme.colorScheme.primary,
  },
  "& a:hover, & a:focus": {
    color: theme.colorScheme.hover,
  },
})

export const linkCss: ColorSchemeCss = theme => ({
  color: theme.colorScheme.accent,
  textDecoration: "underline",
  textDecorationColor: theme.colorScheme.primary,
  textUnderlineOffset: 2,

  "&:hover, &:focus": {
    color: theme.colorScheme.hover,
    textDecorationColor: theme.colorScheme.accent,
  },
})

// container styles for the "Icon / Eyebrow / Title" combo
// we're using in
export const iconEyebrowHeadingCss: ThemeCss = theme => ({
  display: "grid",
  gap: theme.space[3],
  maxWidth: maxSectionWidth.sectionTitle,
})

export const mainContainerCss: ThemeCss = _theme => ({
  display: "grid",
  marginLeft: "auto",
  marginRight: "auto",
})

// used to align an element and its text according to the value set in contentful
export const alignmentCss = (alignment: string) => ({
  textAlign: alignment as Alignment,
  marginLeft: alignment === Alignment.Left ? `0` : `auto`,
  marginRight: alignment === Alignment.Right ? `0` : `auto`,
})
